/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { ReactElement, useEffect, useRef, useState } from 'react';

import {
  Button,
  NullIcon,
  ArrowForwardIosIcon,
  Heading,
  PictureAsPdfIcon,
  FileIcon,
  DescriptionIcon,
} from 'elmo-elements';

import { ResourceMetadataType, ResourceType } from '@/modules/types/resource';

export interface ResourcesViewType {
  isVisible: boolean;
  resources: undefined | null | ResourceType[];
  closeResources: () => void;
}

const ResourcesView = ({
  isVisible,
  resources,
  closeResources,
}: ResourcesViewType): ReactElement => {
  const [isResourcesButtonDisabled, setIsResourcesButtonDisabled] = useState({
    up: true,
    down: true,
  });

  const resourcesRef = useRef<HTMLDivElement>(null);

  const setScrollButtons = () => {
    const resourcesElement = resourcesRef.current;

    if (resourcesElement)
      setIsResourcesButtonDisabled({
        up: resourcesElement.scrollTop <= 0,
        down:
          resourcesElement.scrollTop >=
          resourcesElement.scrollHeight - resourcesElement.offsetHeight,
      });
  };

  useEffect(() => {
    const resourcesElement = resourcesRef.current;

    if (
      resourcesElement &&
      !resourcesElement.hasAttribute('scrollEventAttached')
    ) {
      resourcesElement.setAttribute(
        'scrollEventAttached',
        'scrollEventAttached',
      ); // to add event listener once only
      resourcesElement.addEventListener('scroll', setScrollButtons);
      setScrollButtons();
    }
  });

  const resourcesButtonHandler = (direction: string) => {
    const resourcesElement = resourcesRef.current;
    if (resourcesElement) {
      const newScrollTop =
        resourcesElement.scrollTop +
        resourcesElement.offsetHeight * (direction === 'up' ? -1 : 1);
      resourcesElement.scrollTop = newScrollTop;
    }
  };

  const renderIcon = (type: string) => {
    switch (type) {
      case ResourceMetadataType.PDF:
        return <PictureAsPdfIcon className="mr-2" />;
      case ResourceMetadataType.WORD:
        return <DescriptionIcon className="mr-2" />;
      case ResourceMetadataType.EXCEL:
        return <DescriptionIcon className="mr-2" />;
      case ResourceMetadataType.POWERPOINT:
        return <DescriptionIcon className="mr-2" />;
      case ResourceMetadataType.LINK:
        return <FileIcon className="mr-2" />;

      default:
        return <FileIcon className="mr-2" />;
    }
  };

  if (!resources) return <div />;

  return (
    <>
      <Button
        id="ResourcesCloseBtn"
        onClick={closeResources}
        className={`module-viewer-resources__btn-background ${
          isVisible && 'is-visible'
        }`}
        isText={true}
      >
        <NullIcon />
      </Button>
      <div
        className={`module-viewer-resources__wrapper ${
          isVisible && 'is-visible'
        }`}
      >
        <Button
          id="ResourcesUpBtn"
          isText={true}
          onClick={() => resourcesButtonHandler('up')}
          isDisabled={isResourcesButtonDisabled.up}
          className="module-viewer-resources__btn-scroll-up"
        >
          <ArrowForwardIosIcon className="icon-rotate-270" />
        </Button>
        <div className="module-viewer-resources" ref={resourcesRef}>
          <Heading htmlTag="h4" isBold={true} className="ml-4">
            Resource Library
          </Heading>
          <ul className="module-viewer-resources__list ml-4">
            {resources.map((resource: ResourceType, index: number) => {
              const { url } = resource.elearningResourceMetadata;
              return (
                <li key={`ResourcesLink-${index}`}>
                  <a
                    href={`${url}`}
                    onClick={closeResources}
                    className="module-viewer-resources__link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {renderIcon(resource.elearningResourceMetadata.type)}
                    <span className="module-viewer-resources__list-title">
                      {resource.name}
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
        <Button
          id="ResourcesDownBtn"
          isText={true}
          isDisabled={isResourcesButtonDisabled.down}
          onClick={() => resourcesButtonHandler('down')}
          className="module-viewer-resources__btn-scroll-down"
        >
          <ArrowForwardIosIcon className="icon-rotate-90" />
        </Button>
      </div>
    </>
  );
};

export default ResourcesView;
